import { Platform } from 'react-native';
import {
  ScanHttpError,
  type PrimaryPhotoStreamEvent,
} from './scanStreamTypes';
import { parseSseStream } from './sseParser';

/**
 * Primary-photo stream client — consumes Server-Sent Events from
 * `GET /inventory/{id}/primary-photo/stream` and yields each event as
 * a strongly-typed {@link PrimaryPhotoStreamEvent}.
 *
 * One stream per accepted item. After `POST /inventory/confirm/batch`
 * returns, fan out N of these in parallel via {@link PrimaryPhotoPool}
 * to get transparent-background RGBA PNG crops for the Done view's
 * hero grid.
 *
 * Event sequence on success:
 *   waiting (×0-N) → primary_photo_ready
 * Or, on backend job failure:
 *   waiting (×0-N) → primary_photo_fallback
 * Or, if the job exceeds the 90s timeout:
 *   waiting (×0-N) → primary_photo_timeout
 *
 * All three terminal events close the stream.
 *
 * Web-only for now — same rationale as `streamScan.ts`: RN 0.72's
 * fetch polyfill doesn't expose ReadableStream. Native support lands
 * in the follow-up via `react-native-sse`.
 *
 * Errors:
 *   - Pre-stream 4xx/5xx → throws {@link ScanHttpError}.
 *   - Mid-stream failure → caller's for-await loop rejects; N
 *     concurrent streams fail independently.
 *   - Abort via `opts.signal` stops iteration cleanly.
 */

const API_BASE_URL =
  process.env.EXPO_PUBLIC_API_BASE_URL || 'https://backend.remembr.xyz';

const AUTH_TOKEN_STORAGE_KEY = 'auth_token';

interface StreamPrimaryPhotoOptions {
  signal?: AbortSignal;
}

const getWebAuthToken = (): string | null => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
};

export async function* streamPrimaryPhoto(
  inventoryItemId: number,
  opts: StreamPrimaryPhotoOptions = {},
): AsyncGenerator<PrimaryPhotoStreamEvent, void, void> {
  if (Platform.OS !== 'web') {
    throw new Error('Primary-photo streaming is web-only in this build');
  }

  const token = getWebAuthToken();
  let res: Response;
  try {
    res = await fetch(
      `${API_BASE_URL}/inventory/${inventoryItemId}/primary-photo/stream`,
      {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal: opts.signal,
      },
    );
  } catch (err) {
    // Network-level failure before the stream could open. Rethrow so
    // the caller's outer catch surfaces a generic connectivity msg.
    throw err;
  }

  if (!res.ok) {
    let detail = res.statusText || 'Request failed';
    try {
      const body = await res.json();
      if (body?.detail) {
        detail = Array.isArray(body.detail)
          ? body.detail
              .map((d: any) => d?.msg || JSON.stringify(d))
              .join(', ')
          : String(body.detail);
      }
    } catch {
      /* keep statusText fallback */
    }
    throw new ScanHttpError(res.status, detail);
  }

  if (!res.body) {
    throw new Error(
      'Primary-photo stream response had no body (browser support gap).',
    );
  }

  const textStream = res.body.pipeThrough(new TextDecoderStream());
  for await (const frame of parseSseStream(textStream)) {
    yield {
      type: frame.event as PrimaryPhotoStreamEvent['type'],
      data: frame.data as any,
    } as PrimaryPhotoStreamEvent;
  }
}

export { ScanHttpError } from './scanStreamTypes';
export type { PrimaryPhotoStreamEvent } from './scanStreamTypes';
